import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Volume2, VolumeX, Link2, ExternalLink } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import Lightbox from "../components/Lightbox";
import VoteChevron from "../components/VoteChevron";
import { getShowcase } from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { formatCardNumber } from "../lib/cardNumber";
import { cardPath, setPath, showcasePath } from "../lib/slug";
import { showcaseMeta, breadcrumbList, showcaseJsonLD } from "../lib/seo";

/**
 * /pokemon/en/sets/{set-slug}/{card-slug}/{number}[/{variant}]/showcase/{submission-id}
 *
 * Deep page for a single approved community submission — image or
 * video (or both). Owner-facing goals (2026-02-01 spec):
 *   - Photography-first: submission media LARGE and central.
 *   - Card identity block above the fold for cold landers.
 *   - Real breadcrumb + link back to the parent card page.
 *   - Reuse the exact existing vote control + total count.
 *   - Copy-permalink affordance (share).
 *
 * Access control: the backend endpoint `/api/v1/showcases/{id}` 404s on
 * pending / rejected / hidden-set — we surface that as a plain 404 view.
 */
export default function ShowcasePage() {
  const { game, lang, setSlug, cardSlug, number, variantSlug, submissionId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();

  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    getShowcase(submissionId)
      .then((data) => {
        if (!alive) return;
        setPayload(data);
        setStatus("ready");
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Not found");
        setStatus("error");
      });
    return () => { alive = false; };
  }, [submissionId]);

  // Canonical-guard: if the URL params don't match the resolved
  // showcase's canonical path, replace-navigate. Keeps share links
  // from drifting after a card rename / variant flip.
  useEffect(() => {
    if (!payload) return;
    const { card, set, variant, default_variant_slug: defaultSlug } = payload;
    if (!card || !set || !variant) return;
    const canonical = showcasePath({
      game,
      lang,
      setName: set.name || set.external_id,
      cardName: card.name,
      number: card.number,
      variantSlug: variant.slug || variant.variant_type,
      defaultVariantSlug: defaultSlug || null,
      submissionId,
    });
    const currentPath =
      `/${game}/${lang}/sets/${setSlug}/${cardSlug}/${number}` +
      (variantSlug ? `/${variantSlug}` : "") +
      `/showcase/${submissionId}`;
    if (canonical !== currentPath) {
      navigate(canonical, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const meta = useMemo(() => (payload ? showcaseMeta(payload) : null), [payload]);
  const jsonld = useMemo(() => (payload && meta ? showcaseJsonLD(payload, meta) : null), [payload, meta]);
  const jsonldBreadcrumb = useMemo(() => {
    if (!payload) return null;
    const { card, set, variant, default_variant_slug } = payload;
    if (!card || !set) return null;
    const variantSlugActive = variant.slug || variant.variant_type || "";
    const isDefault = default_variant_slug && variantSlugActive === default_variant_slug;
    const variantLabel = variant.variant_label;
    const cardCrumbName = card.name + (isDefault || !variantLabel ? "" : ` · ${variantLabel}`);
    const showcaseName = `${cardCrumbName} — showcase by ${payload.user?.display_name || payload.user_display_name || "Collector"}`;
    return breadcrumbList({
      items: [
        { name: "Pokémon", path: "/pokemon" },
        { name: "English Sets", path: "/pokemon/en/sets" },
        { name: set.name, path: setPath(game, lang, set.name || set.external_id) },
        {
          name: cardCrumbName,
          path: cardPath({
            game, lang,
            setName: set.name,
            cardName: card.name,
            number: card.number,
            variantSlug: variantSlugActive,
            defaultVariantSlug: default_variant_slug,
          }),
        },
        { name: showcaseName, path: null },
      ],
    });
  }, [payload, game, lang]);

  function openImage(src) { setLightbox({ kind: "image", src }); }
  function openVideo() {
    if (!payload?.video) return;
    setLightbox({
      kind: "video",
      src: payload.video.mp4_url || payload.video.preview_url,
      poster: payload.video.poster_url,
    });
  }

  function copyPermalink() {
    if (typeof window === "undefined") return;
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Couldn't copy"));
  }

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-8 md:px-gutter md:py-10"
      data-testid="showcase-page"
    >
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta name="robots" content={meta.robots} />
          <link rel="canonical" href={meta.canonical} />
          <meta property="og:site_name" content="CardCapture" />
          <meta property="og:title" content={meta.title} />
          <meta property="og:description" content={meta.description} />
          <meta property="og:url" content={meta.canonical} />
          <meta property="og:type" content={meta.ogType} />
          <meta property="og:image" content={meta.ogImage} />
          {meta.videoUrl && <meta property="og:video" content={meta.videoUrl} />}
          {meta.videoUrl && <meta property="og:video:url" content={meta.videoUrl} />}
          {meta.videoUrl && <meta property="og:video:secure_url" content={meta.videoUrl} />}
          {meta.videoType && <meta property="og:video:type" content={meta.videoType} />}
          {meta.videoWidth && <meta property="og:video:width" content={String(meta.videoWidth)} />}
          {meta.videoHeight && <meta property="og:video:height" content={String(meta.videoHeight)} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={meta.title} />
          <meta name="twitter:description" content={meta.description} />
          <meta name="twitter:image" content={meta.ogImage} />
          {jsonld && jsonld.map((obj) => (
            <script key={obj["@type"] || JSON.stringify(obj).slice(0, 32)} type="application/ld+json">
              {JSON.stringify(obj)}
            </script>
          ))}
          {jsonldBreadcrumb && (
            <script type="application/ld+json">
              {JSON.stringify(jsonldBreadcrumb)}
            </script>
          )}
        </Helmet>
      )}

      {status === "loading" && (
        <div className="mt-16 text-body text-ink-muted" data-testid="showcase-loading">
          Loading showcase…
        </div>
      )}

      {status === "error" && (
        <div className="mt-16 max-w-[720px]" data-testid="showcase-error">
          <div className="text-eyebrow uppercase text-ink-muted">404</div>
          <h1 className="mt-1 text-title-page text-ink-text">Showcase not found</h1>
          <p className="mt-2 text-body text-ink-muted">{error}</p>
          <Link to="/pokemon/en/sets" className="mt-6 inline-block text-indigo-text hover:underline">
            Back to sets
          </Link>
        </div>
      )}

      {status === "ready" && payload && payload.card && payload.set && payload.variant && (
        <ShowcaseBody
          payload={payload}
          game={game}
          lang={lang}
          muted={muted}
          setMuted={setMuted}
          onOpenImage={openImage}
          onOpenVideo={openVideo}
          onCopyLink={copyPermalink}
          viewer={auth.user}
        />
      )}

      <Lightbox open={!!lightbox} item={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function ShowcaseBody({ payload, game, lang, muted, setMuted, onOpenImage, onOpenVideo, onCopyLink, viewer }) {
  const { card, set, variant, default_variant_slug } = payload;
  const collectorName = payload.user?.username || payload.user?.display_name || payload.user_display_name || "Collector";
  const cardHref = cardPath({
    game, lang,
    setName: set.name,
    cardName: card.name,
    number: card.number,
    variantSlug: variant.slug || variant.variant_type,
    defaultVariantSlug: default_variant_slug,
  });
  const hasVideo = payload.video && payload.video.transcode_status === "ready";
  const hasImage = !!payload.image;

  const numberDisplay = formatCardNumber({
    localId: card.number,
    cardCountOfficial: set.card_count_official,
    setCode: set.code || set.external_id,
  });

  const altText = `${card.name} ${card.number}${variant.variant_label ? ` ${variant.variant_label}` : ""} — collector showcase`;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Pokémon", to: "/pokemon" },
          { label: "English", to: "/pokemon/en/sets" },
          { label: set.name, to: setPath(game, lang, set.name || set.external_id) },
          { label: `#${card.number} · ${card.name}${variant.variant_label ? ` · ${variant.variant_label}` : ""}`, to: cardHref },
          { label: `Showcase · ${collectorName}` },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          {/* Media stack — image first (primary), video after; both if present */}
          <div className="flex flex-col gap-4" data-testid="showcase-media-stack">
            {hasImage && (
              <button
                type="button"
                data-testid="showcase-image"
                onClick={() => onOpenImage(payload.image.url)}
                className="group overflow-hidden rounded-card border border-ink-chip bg-ink-surface"
              >
                <img
                  src={payload.image.url}
                  alt={altText}
                  className="block h-auto max-h-[85vh] w-full cursor-zoom-in object-contain"
                  loading="eager"
                />
              </button>
            )}
            {hasVideo && (
              <div
                data-testid="showcase-video-frame"
                className="relative overflow-hidden rounded-card border border-ink-chip bg-black"
              >
                <video
                  data-testid="showcase-video"
                  src={payload.video.preview_url || payload.video.mp4_url}
                  poster={payload.video.poster_url}
                  onClick={onOpenVideo}
                  muted={muted}
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                  className="block max-h-[85vh] w-full cursor-zoom-in object-contain"
                />
                <button
                  data-testid="showcase-video-mute"
                  onClick={() => setMuted((m) => !m)}
                  className="absolute right-3 top-3 rounded-chip border border-ink-chip bg-ink-bg/75 p-1.5 text-ink-muted backdrop-blur-sm hover:text-ink-text"
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-[92px]">
          {/* Card identity block for cold landers */}
          <section
            data-testid="showcase-card-identity"
            className="rounded-card border border-ink-chip bg-ink-surface p-4"
          >
            <div className="text-eyebrow uppercase text-ink-muted">Showcase from</div>
            <Link
              to={cardHref}
              data-testid="showcase-card-link"
              className="mt-1 block text-title-section text-ink-text hover:text-indigo-text"
            >
              {card.name}
              {variant.variant_label ? (
                <span className="ml-1 text-ink-muted"> · {variant.variant_label}</span>
              ) : null}
            </Link>
            <div className="mt-1 text-meta text-ink-muted">
              <Link
                to={setPath(game, lang, set.name || set.external_id)}
                className="hover:text-indigo-text"
              >
                {set.name}
              </Link>
              {numberDisplay ? ` · ${numberDisplay}` : ""}
              {card.rarity ? ` · ${card.rarity}` : ""}
            </div>
          </section>

          {/* Collector row */}
          <section
            data-testid="showcase-collector"
            className="mt-3 rounded-card border border-ink-chip bg-ink-surface p-4"
          >
            <div className="text-eyebrow uppercase text-ink-muted">Collector</div>
            <div className="mt-2 flex items-center gap-3">
              {payload.user?.avatar_url && (
                <img
                  src={payload.user.avatar_url}
                  alt=""
                  className="h-10 w-10 rounded-full object-cover"
                />
              )}
              <Link
                to={`/users/${payload.user_id}`}
                data-testid="showcase-collector-link"
                className="text-body text-ink-text hover:text-indigo-text"
              >
                {collectorName}
              </Link>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-meta text-ink-muted">Upvotes</div>
              <VoteChevron submission={payload} viewer={viewer} onChange={() => { /* server-owned; re-fetch handled elsewhere */ }} size="lg" />
            </div>
          </section>

          {/* Share affordance */}
          <section
            data-testid="showcase-share"
            className="mt-3 flex gap-2"
          >
            <button
              type="button"
              onClick={onCopyLink}
              data-testid="showcase-copy-link"
              className="flex flex-1 items-center justify-center gap-2 rounded-card border border-ink-chip bg-ink-surface px-3 py-2 text-meta text-ink-text transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
            >
              <Link2 size={14} />
              Copy link
            </button>
            <Link
              to={cardHref}
              data-testid="showcase-back-to-card"
              className="flex items-center justify-center gap-2 rounded-card border border-ink-chip bg-ink-surface px-3 py-2 text-meta text-ink-text transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
            >
              <ExternalLink size={14} />
              Card page
            </Link>
          </section>
        </aside>
      </div>
    </>
  );
}
